import Button from '@components/Button';
import { showConfirmModal } from '@components/ConfirmModal';
import Modal from '@components/Modal';
import TextLink from '@components/TextLink';
import { TipWrapper } from '@components/TipWrapper';
import Title from '@components/Title';
import { API_DOMAIN, VIP_URL } from '@constants';
import { $tourData } from '@stores/dataStores';
import { $isVip } from '@stores/memberStores';
import { TemplateId } from '@types';
import axios from '@utils/axios';
import classNames from 'classnames';
import { useStore, useStoreMap } from 'effector-react';
import { useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { TEMPLATES_CONFIG } from '../constants';

interface Props {
  templateId?: TemplateId;
  onConfirm: (id: TemplateId) => void;
  onRequestClose: () => void;
}

const StyledModal = styled(Modal)`
  width: calc(100% - 60px);
  height: calc(100% - 60px);
  min-width: 1000px;
  min-height: 550px;
`;
const TempThumb = styled.img`
  width: 210px;
  height: 90px;
`;

const ids = [
  TemplateId.legacy,
  TemplateId.vip,
  TemplateId.simple,
  TemplateId.project,
  TemplateId.realEstateA,
  TemplateId.realEstateB,
  TemplateId.metaverseA,
];

function TemplateModal({ templateId, onConfirm, onRequestClose }: Props) {
  const { data: templateIds, isLoading } = useQuery('template', async () => {
    const string = await axios.get<string>(`${API_DOMAIN}/member/my/templates`);
    return (string?.split(',')?.map(Number) ?? []) as TemplateId[];
  });
  const [selected, setSelected] = useState(templateId);
  const isVipMember = useStore($isVip);
  const isVipProd = useStoreMap({
    store: $tourData,
    keys: [],
    fn: (data) => data.expired === 0,
  });
  const isVip = isVipMember || isVipProd;
  const config = selected === undefined ? null : TEMPLATES_CONFIG[selected];
  const isTrial = (id: TemplateId) => {
    if (isVip) return false;
    if (templateIds?.includes(id)) return false;
    return !TEMPLATES_CONFIG[id].isFree;
  };

  const handleSelect = async () => {
    if (selected === undefined) return;
    const { confirmed } = await showConfirmModal({
      title: '温馨提示',
      content: `不同模板具体不同功能，使用新模板后之前模板中设置的部分功能可能失效，是否确认切换至“${config?.name}”模板？`,
      buttonText: '确认切换',
    });
    if (confirmed) {
      onRequestClose();
      onConfirm(selected);
    }
  };

  return (
    <StyledModal
      isOpen
      onRequestClose={onRequestClose}
      header={
        <div className="flex items-end">
          <Title>作品模板库</Title>
          {!isVip && (
            <>
              <div className="ml-20">解锁全部模板，立享增值服务</div>
              <TextLink className="ml-10" href={VIP_URL} target="_blank">
                {'开通会员服务 >>'}
              </TextLink>
            </>
          )}
        </div>
      }
    >
      {!isLoading && (
        <div className="flex h-full pb-20">
          <div className="flex flex-1 flex-col">
            {config ? (
              <iframe
                title="template"
                className="w-full flex-1 rounded"
                src={config.demo}
              />
            ) : (
              <div className="flex w-full flex-1 items-center justify-center rounded bg-white-light">
                请从右侧选择要使用的模板
              </div>
            )}
            <div className="mt-10 flex h-55 items-center rounded bg-white-light px-10">
              <div className="flex-1 truncate">
                <div className="text-lg">
                  {config ? config.name : '还未选择模板'}
                </div>
                <div
                  className="truncate text-sm text-black-gray"
                  title={config ? config.desc : '~'}
                >
                  {config ? config.desc : '~'}
                </div>
              </div>
              {templateId === selected && selected !== undefined && (
                <div className="ml-20 flex h-30 flex-shrink-0 select-none items-center rounded bg-orange px-10 text-white">
                  {isTrial(selected) ? '免费试用中' : '使用中'}
                </div>
              )}
              {templateId !== selected && selected !== undefined && (
                <TipWrapper
                  multiline
                  text={
                    isTrial(selected)
                      ? '试用期间仅支持在作品编辑时预览，前端播放页始终用经典免费版展示，开通会员后立享全功能展示'
                      : ''
                  }
                >
                  <Button
                    className="ml-20 flex-shrink-0"
                    onClick={handleSelect}
                  >
                    {isTrial(selected) ? '免费试用模板' : '使用模板'}
                  </Button>
                </TipWrapper>
              )}
            </div>
          </div>
          <div className="ml-20 w-210 flex-shrink-0">
            <Scrollbars>
              {ids.map((id) => (
                <div
                  key={id}
                  className="relative mb-10 cursor-pointer"
                  onClick={() => setSelected(id)}
                >
                  <TempThumb
                    className={classNames('rounded', {
                      'ring-2 ring-orange': id === selected,
                    })}
                    src={TEMPLATES_CONFIG[id].thumb}
                  />
                  {id === templateId && (
                    <div className="absolute right-0 top-0 flex h-20 items-center rounded-tr bg-orange px-5 text-sm text-white">
                      {isTrial(templateId) ? '免费试用中' : '使用中'}
                    </div>
                  )}
                </div>
              ))}
            </Scrollbars>
          </div>
        </div>
      )}
    </StyledModal>
  );
}

export default TemplateModal;
